import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';

export default function ContactPage() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async () => {
    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create form data for Netlify
      const formBody = new URLSearchParams({
        'form-name': 'contact',
        ...formData
      }).toString();

      // Submit to Netlify Forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'An error occurred. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]}>
      <Text style={styles.title}>Contact Us</Text>

      {/* Hidden form for Netlify to detect */}
      <form name="contact" data-netlify="true" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="text" name="subject" />
        <textarea name="message"></textarea>
      </form>

      <View style={styles.content}>
        <View style={[styles.formContainer, isMobile && styles.formContainerMobile]}>
          <Text style={styles.paragraph}>
            Have questions, suggestions, or feedback about our Gematria Calculator? We'd love to hear from you!
            Fill out the form below and we'll get back to you as soon as possible.
          </Text>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Your Name</Text>
            <TextInput
              style={styles.input}
              value={formData.name}
              onChangeText={(text) => setFormData({ ...formData, name: text })}
              placeholder="Enter your name"
              placeholderTextColor={colors.textSecondary}
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Email Address</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text })}
              placeholder="Enter your email"
              placeholderTextColor={colors.textSecondary}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Subject</Text>
            <TextInput
              style={styles.input}
              value={formData.subject}
              onChangeText={(text) => setFormData({ ...formData, subject: text })}
              placeholder="Enter subject"
              placeholderTextColor={colors.textSecondary}
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Your Message</Text>
            <TextInput
              style={[styles.input, styles.textarea]}
              value={formData.message}
              onChangeText={(text) => setFormData({ ...formData, message: text })}
              placeholder="Enter your message"
              placeholderTextColor={colors.textSecondary}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
            />
          </View>

          {submitStatus && (
            <View style={[
              styles.statusMessage,
              submitStatus.type === 'success' ? styles.successMessage : styles.errorMessage
            ]}>
              <Text style={[
                styles.statusText,
                submitStatus.type === 'success' ? styles.successText : styles.errorText
              ]}>{submitStatus.message}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={isSubmitting}
          >
            <Text style={[styles.submitButtonText, isSubmitting && styles.submitButtonTextDisabled]}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.faqSection}>
          <Text style={styles.heading}>Frequently Asked Questions</Text>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How accurate is the Gematria Calculator?</Text>
            <Text style={styles.faqAnswer}>
              Our calculator implements traditional gematria ciphers with high accuracy. We regularly review and
              update our algorithms to ensure they align with established numerological systems.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I suggest a new cipher to be added?</Text>
            <Text style={styles.faqAnswer}>
              Absolutely! We welcome suggestions for new ciphers. Please use the contact form above and provide
              details about the cipher you'd like to see added.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is there an API available for the calculator?</Text>
            <Text style={styles.faqAnswer}>
              We're currently developing an API for developers who want to integrate our gematria calculations
              into their applications. Contact us for more information about beta access.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How can I report a bug or issue?</Text>
            <Text style={styles.faqAnswer}>
              If you encounter any bugs or issues while using our calculator, please let us know through the
              contact form. Include as much detail as possible, including the text you entered, the browser
              you're using, and any error messages you received.
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    padding: 40,
  },
  containerMobile: {
    padding: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 30,
    textAlign: 'center',
  },
  content: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  formContainer: {
    backgroundColor: colors.surface,
    padding: 30,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 40,
  },
  formContainerMobile: {
    padding: 20,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 25,
    color: colors.text,
    marginBottom: 25,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    padding: 12,
    fontSize: 16,
    color: colors.text,
    backgroundColor: colors.inputBackground,
  },
  textarea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: colors.primary,
    padding: 15,
    borderRadius: 4,
    alignItems: 'center',
    marginTop: 10,
  },
  submitButtonDisabled: {
    backgroundColor: colors.secondary,
  },
  submitButtonText: {
    color: colors.onPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
  submitButtonTextDisabled: {
    color: colors.onSecondary,
  },
  statusMessage: {
    padding: 15,
    borderRadius: 4,
    marginBottom: 15,
  },
  successMessage: {
    backgroundColor: colors.successSurface,
    borderWidth: 1,
    borderColor: colors.success,
  },
  errorMessage: {
    backgroundColor: colors.dangerSurface,
    borderWidth: 1,
    borderColor: colors.error,
  },
  statusText: {
    fontSize: 14,
  },
  successText: {
    color: colors.success,
  },
  errorText: {
    color: colors.error,
  },
  faqSection: {
    marginTop: 20,
    marginBottom: 40,
  },
  heading: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  faqItem: {
    marginBottom: 25,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  faqQuestion: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 10,
  },
  faqAnswer: {
    fontSize: 15,
    lineHeight: 24,
    color: colors.textSecondary,
  },
});
